import { Box } from '@dtx-company/shared-components/src'
import { CovidFormFields } from './types'
import { FormProvider, useForm } from 'react-hook-form-deprecated'
import { ProgressBar } from './ProgressBar'
import { animate, useMotionValue } from 'framer-motion'
import { useIsMobile } from '../../utils/main'
import ContactInfoForm from './ContactInfoForm'
import CovidQuestion from './QuestionForm'
import FinishStep from './FinishStep'
import React from 'react'
import covidQuestions from './covidQuestions'
import styled from 'styled-components'
import submitCheckIn from './submitCheckInMutation'

interface CovidForm {
  dname?: string
  phone?: string
  email?: string
  questions: {
    isInCloseContact: boolean | null
    isIsolating: boolean | null
    isSymptomatic: boolean | null
    isWaitingOnResults: boolean | null
  }
  address?: {
    streetAddress1: string
    streetAddress2: string
    city: string
    region: string
    postalCode: string
  }
}

const STEP_MARGIN = 24

const StepWrapper = styled(Box)`
  & > :not(:last-child) {
    margin-right: ${STEP_MARGIN}px;
  }
`

const Form = styled.form`
  display: flex;
  flex-grow: 1;
  padding: 0px 16px;
  justify-content: center;
`

const Step: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <Box flexDirection="column" width="100%" flexShrink={0} height="100%">
    {children}
  </Box>
)

const intOfBool = (b: boolean): number => +b

interface CovidFormProps {
  fields: Set<string> | null
  id: string
  onFinished: () => void
}

const CovidForm: React.FC<CovidFormProps> = ({ fields: formFields, id, onFinished }) => {
  const [curStep, setCurStep] = React.useState(0)
  const [hasCovid, setHasCovid] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)
  const fields = useForm({
    mode: 'onTouched',
    defaultValues: {
      questions: {
        isInCloseContact: null,
        isIsolating: null,
        isSymptomatic: null,
        isWaitingOnResults: null
      }
    }
  })
  const mobile = useIsMobile()
  const onSubmit = async ({ dname, ...response }: CovidForm): Promise<void> => {
    await submitCheckIn({ ...response, displayName: dname, widgetId: id })
    onFinished()
  }
  const cardWidth = mobile ? 340 : 500
  const scroll = useMotionValue(0)
  const changeSteps = React.useCallback(
    (s: number) => animate(scroll, s * (cardWidth + STEP_MARGIN)),
    [cardWidth, scroll]
  )
  const hasContactInfoForm = React.useMemo(
    () =>
      [CovidFormFields.dname, CovidFormFields.email, CovidFormFields.phone].some(e =>
        formFields?.has(e)
      ),
    [formFields]
  )
  const nSteps = React.useMemo(
    () =>
      intOfBool(hasContactInfoForm) +
      Object.keys(covidQuestions).length +
      intOfBool(formFields?.has(CovidFormFields.address) || false) +
      1,
    // TODO: resolve this rule of hooks error - issue tracked in https://app.shortcut.com/flowcode/story/41455/resolve-rule-of-hooks-error-that-were-commented-out
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formFields]
  )
  const advanceStep = async (): Promise<void> => {
    if (curStep + 1 === nSteps - 1) {
      await fields.handleSubmit(onSubmit)()
    }
    changeSteps(curStep + 1)
    setCurStep(curStep + 1)
  }

  React.useEffect(
    () => {
      const unsub = scroll.onChange(s => {
        if (ref.current) {
          ref.current.scrollLeft = s
        }
      })
      return unsub
    }, // TODO: resolve this rule of hooks error - issue tracked in https://app.shortcut.com/flowcode/story/41455/resolve-rule-of-hooks-error-that-were-commented-out
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const endQuestions = React.useCallback(
    async () => {
      await fields.handleSubmit(onSubmit)()
      setHasCovid(true)
      setCurStep(nSteps - 1)
      changeSteps(nSteps - 1)
    }, // TODO: resolve this rule of hooks error - issue tracked in https://app.shortcut.com/flowcode/story/41455/resolve-rule-of-hooks-error-that-were-commented-out
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nSteps, changeSteps]
  )
  return (
    <FormProvider {...fields}>
      {curStep !== nSteps - 1 && (
        <ProgressBar
          percentage={((curStep + 1) / nSteps) * 100}
          borderRadius="4px"
          mb="24px"
          height={20}
        />
      )}
      <Form onSubmit={fields.handleSubmit(onSubmit)}>
        {formFields !== null && (
          <StepWrapper overflow="hidden" width={cardWidth} ref={ref}>
            {hasContactInfoForm && (
              <Step>
                <ContactInfoForm advanceStep={advanceStep} fields={formFields} />
              </Step>
            )}
            {Object.entries(covidQuestions).map(([k, v]) => (
              <Step key={k}>
                <CovidQuestion
                  qName={k}
                  El={v}
                  advanceStep={advanceStep}
                  endQuestions={endQuestions}
                />
              </Step>
            ))}
            <Step>
              <FinishStep hasCovid={hasCovid} />
            </Step>
          </StepWrapper>
        )}
      </Form>
    </FormProvider>
  )
}

export default CovidForm
